import { Component } from '@theme/component';
import { debounce, onDocumentLoaded, setHeaderMenuStyle } from '@theme/utilities';
import { MegaMenuHoverEvent } from '@theme/events';

/** Skim filter: pointer must dwell this long before MegaMenuHoverEvent fires. */
const HOVER_COMMIT_DELAY_MS = 150;

/**
 * How long after a menu item activates to suppress #reconcilePointerTarget.
 * Matches --submenu-animation-speed (base.css, 360ms) with headroom: while a
 * panel is still animating open, elementFromPoint at the pointer's position
 * can momentarily still resolve into the PREVIOUS panel's DOM (its
 * visibility/clip-path haven't finished updating), so a hit-test reconcile
 * landing in that window can re-activate the item the user just left —
 * live-reproduced in Chrome as the wrong dropdown showing while moving
 * straight from a trigger down into that trigger's own (correct) submenu,
 * most reliably when the pointer settles inside the panel (which is exactly
 * when reconciliation runs). Real cross-item switches don't rely on this
 * reconciliation in the first place — they activate via a genuine
 * pointerenter on the new trigger — so suppressing it briefly after every
 * activation costs nothing.
 */
const RECONCILE_SUPPRESS_MS = 400;

/**
 * Grace period between a `deactivate()` call whose guards didn't match and
 * the actual close. Live-instrumented 2026-09-13 (real pointer input, not
 * synthetic): even after fixing deactivate()'s guards to correctly detect
 * "pointer moved into the menu" (see that method's own comment), an
 * overflow-redirected item (hovering "More" down into Explore's own panel)
 * could still close on a real, single `pointerleave` whose `relatedTarget`
 * had NO `.menu-list__list-item` ancestor at all — i.e. the pointer's exact
 * hit-tested element genuinely wasn't part of the menu at that instant. Two
 * confirmed causes: a real screen-space dead zone between the "More"
 * trigger and the panel below it, and the panel's own `clip-path` reveal
 * (360ms, --submenu-animation-speed) — the area outside the current clip
 * isn't hit-testable, so a pointer arriving before that animation finishes
 * hits whatever's behind the panel instead of the panel itself. `#deactivate`
 * previously ran synchronously the instant either happened, with zero
 * tolerance, and also tore down pointer tracking on the way out — so the
 * `#reconcilePointerTarget` self-heal that might otherwise catch this never
 * got a chance to run. A short grace period before actually closing (any
 * fresh `activate()` cancels it — see #deactivateTimer) bridges exactly
 * this: real mouse travel from trigger to panel link takes longer than this
 * window, so a genuine move-away still closes promptly, but a momentary
 * miss mid-transition doesn't.
 */
const DEACTIVATE_GRACE_MS = 150;

/**
 * Hover intent for switching from one open item to another (2026-09-22).
 * Every trigger → panel path that isn't straight down crosses neighbouring
 * triggers while still inside the trigger row, and each crossing fires a
 * genuine pointerenter on that neighbour — e.g. Explore (rightmost) to its
 * own links (at the page's left margin) crosses Binders on the way. The
 * theme's own diagonal "safety box" can't absorb this any more: it was made
 * `pointer-events: none` on 2026-09-12 (blocks/_header-menu.liquid) because
 * it intercepted the real triggers. So while an item is already open, a
 * pointer-driven switch only commits once the pointer settles on the new
 * trigger — under SWITCH_INTENT_SENSITIVITY_PX of travel over one
 * SWITCH_INTENT_INTERVAL_MS poll — or has stayed over it for
 * SWITCH_INTENT_MAX_MS without heading down toward the panel. A pointer
 * passing through a neighbour on its way into the panel does neither, so
 * the open panel stays put; the heading check is what keeps a slow
 * diagonal (which can spend longer than SWITCH_INTENT_MAX_MS crossing a
 * neighbouring trigger) from switching anyway.
 * Opening from closed and keyboard focus are unaffected (immediate).
 */
const SWITCH_INTENT_INTERVAL_MS = 30;
const SWITCH_INTENT_SENSITIVITY_PX = 2;
const SWITCH_INTENT_MAX_MS = 250;
/** Minimum dy/|dx| over one poll for movement to count as heading down into the panel. */
const SWITCH_INTENT_DOWNWARD_SLOPE = 0.1;

/**
 * A custom element that manages a header menu.
 *
 * @typedef {Object} State
 * @property {HTMLElement | null} activeItem - The currently active menu item.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} overflowMenu - The overflow menu.
 * @property {HTMLElement[]} [submenu] - The submenu in each respective menu item.
 *
 * @extends {Component<Refs>}
 */
class HeaderMenu extends Component {
  requiredRefs = ['overflowMenu'];

  /**
   * @type {MutationObserver | null}
   */
  #submenuMutationObserver = null;

  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #hoverDispatchTimer;

  connectedCallback() {
    super.connectedCallback();

    onDocumentLoaded(this.#preloadImages);
    window.addEventListener('resize', this.#resizeListener);
    this.overflowMenu?.addEventListener('pointerleave', this.#overflowSubmenuListener);
    this.addEventListener('pointerenter', this.#handleDirectPointerEnter, { capture: true });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener('resize', this.#resizeListener);
    document.body.removeEventListener('pointermove', this.#onPointerMove);
    if (this.#state.activeItem) {
      this.#stopPointerTracking(this.#state.activeItem);
    }
    this.overflowMenu?.removeEventListener('pointerleave', this.#overflowSubmenuListener);
    this.removeEventListener('pointerenter', this.#handleDirectPointerEnter, { capture: true });
    this.#cleanupMutationObserver();
    clearTimeout(this.#hoverDispatchTimer);
    this.#hoverDispatchTimer = undefined;
    clearTimeout(this.#deactivateTimer);
    this.#deactivateTimer = undefined;
    this.#cancelSwitch();
  }

  /**
   * 2026-09-13: supplements the framework's own `on:pointerenter="/activate"`
   * dispatch (assets/component.js's `registerEventListeners`), which — for
   * pointerenter/pointerleave specifically — requires `event.target` to be
   * an EXACT match for the bound element (no `.closest()` fallback there,
   * by design, since a document-wide listener doing `.closest()` on every
   * pointerenter in the whole page would be expensive).
   *
   * Live-captured in Chrome with real mouse input: pointerenter reliably
   * fires (confirmed via a raw capture-phase listener) on the trigger's
   * nested children — the link, the label span — while the exact-match
   * dispatch upstream sometimes never resolves activate() for the actual
   * `<li>` itself. Nothing then visibly opens until this component's own
   * #reconcilePointerTarget() safety net (which exists for a different,
   * already-documented Chrome/Safari hit-testing gap — see
   * RECONCILE_SUPPRESS_MS) eventually re-fires it — up to
   * RECONCILE_SUPPRESS_MS (400ms) plus the pointer-idle wait (50ms) later.
   * Measured live: 250-450ms of visible lag/flicker per occurrence, Chrome
   * only — not reproduced in Safari.
   *
   * This listener is scoped to the header-menu component only (not
   * document-wide), so `.closest()` here doesn't carry the cost the
   * framework's blanket exemption was written to avoid. It resolves the
   * same `.menu-list__list-item` the exact-match path would have, via the
   * same `findMenuItem()` used by activate() itself, and activates
   * immediately. Calling activate() again when the framework's own dispatch
   * *did* land is a harmless no-op — activate() already early-returns when
   * the resolved item is already active.
   */
  #handleDirectPointerEnter = (event) => {
    if (!(event.target instanceof Element)) return;
    // activate() only receives a plain object from here, so #scheduleSwitch
    // can't read coordinates off it — seed #lastPointer instead (see the
    // baseline comment there).
    if (event.isTrusted) this.#lastPointer = { x: event.clientX, y: event.clientY };
    const listItem = event.target.closest('.menu-list__list-item');
    if (!listItem) return;
    this.activate(/** @type {PointerEvent} */ ({ target: listItem, pointerType: event.pointerType }));
  };

  /**
   * Debounced resize event listener to recalculate menu style.
   *
   * Also forces the overflow list to fully re-measure. Its own ResizeObserver
   * watches its own box, which shrinks to fit its (now smaller) content as
   * soon as items move into the overflow slot — once that settles, growing
   * the window back doesn't change the box size any further (nothing gives
   * it flex-grow), so the observer never fires again and overflowed items
   * never come back on their own. Dispatching 'reflow' here re-measures
   * against the current available width on every resize, independent of
   * whether the overflow-list's own box happened to change size.
   *
   * Skipped while an overflow item is the active/hovered one
   * (data-overflow-expanded='true'): #reflowItems() reassigns every
   * overflowing element's `slot` from scratch, including the one the user
   * currently has open, which desyncs it from this component's own
   * activeItem/aria-expanded state (the item silently moves to the flat nav
   * while the panel stays open showing it as active) — a visible glitch if a
   * resize happens to land mid-hover. Deferring until the interaction ends
   * avoids that; the next resize (or reopening "More") catches up.
   */
  #resizeListener = debounce(() => {
    setHeaderMenuStyle();
    if (this.dataset.overflowExpanded === 'true') return;
    this.refs.overflowMenu?.dispatchEvent(new CustomEvent('reflow', { detail: {} }));
  }, 100);

  // Same grace-period reasoning as deactivate() itself (see
  // DEACTIVATE_GRACE_MS) — this fires on pointerleave of the whole shadow
  // `[part="overflow"]` box, which is exactly as susceptible to a dead-zone/
  // clip-path-timing miss as the per-item case.
  #overflowSubmenuListener = () => {
    const itemToDeactivate = this.#state.activeItem;
    clearTimeout(this.#deactivateTimer);
    this.#deactivateTimer = setTimeout(() => {
      this.#deactivateTimer = undefined;
      this.#deactivate(itemToDeactivate ?? undefined);
    }, DEACTIVATE_GRACE_MS);
  };

  /**
   * @type {State}
   */
  #state = {
    activeItem: null,
  };

  /**
   * @type {ReturnType<typeof setTimeout> | undefined}
   */
  #pointerIdleTimer;

  /**
   * Pending grace-period close scheduled by deactivate() — see
   * DEACTIVATE_GRACE_MS. Cancelled by any fresh activate() call.
   * @type {ReturnType<typeof setTimeout> | undefined}
   */
  #deactivateTimer;

  /**
   * Pending hover-intent switch to a different item — see
   * SWITCH_INTENT_INTERVAL_MS.
   * @type {ReturnType<typeof setTimeout> | undefined}
   */
  #switchTimer;

  /** @type {HTMLElement | null} */
  #pendingSwitchItem = null;

  /**
   * Last known pointer position — used both for hit-test reconciliation and
   * to derive our own movement delta (see #onPointerMove).
   * @type {{ x: number, y: number }}
   */
  #lastPointer = { x: 0, y: 0 };

  /**
   * performance.now() timestamp of the last activate() switch to a new item.
   * See RECONCILE_SUPPRESS_MS.
   * @type {number}
   */
  #activatedAt = 0;

  /**
   * Update the safety box idle state on the active menu item.
   * @param {PointerEvent} event
   */
  #onPointerMove = (event) => {
    // Root fix for the mega-menu "wrong panel" bug (2026-09-21): continuously
    // re-syncs the active panel to whichever top-level trigger's own
    // bounding rect the pointer's clientX is currently under, rather than
    // relying solely on the discrete pointerenter events #handleDirectPointerEnter
    // and the framework's own dispatch depend on. Runs first, before the
    // safety-box tracking below reads #state.activeItem, so that tracking
    // always operates on the freshly-synced item rather than a stale
    // reference from before this tick's correction (if any). See
    // #reconcileActiveTriggerByPosition's own doc for the full mechanism.
    this.#reconcileActiveTriggerByPosition(event.clientX, event.clientY);

    const activeLink = this.#state.activeItem;
    if (!activeLink) return;

    // Derive the delta from clientX/Y ourselves instead of trusting
    // event.movementX/movementY: Chrome computes movementX/Y from raw
    // pointer-device deltas, which can read as exactly 0 on a tick where
    // clientX/Y has still visibly moved (most reproducible during slow,
    // continuous real mouse motion) — Safari's is a plain prev/current
    // coordinate diff and doesn't share this gap. Under-reporting a tick as
    // "not moving" mid-motion fires #reconcilePointerTarget more often than
    // intended; see RECONCILE_SUPPRESS_MS for why that also needed its own
    // guard against a transition-timing race.
    const deltaX = event.clientX - this.#lastPointer.x;
    const deltaY = event.clientY - this.#lastPointer.y;
    this.#lastPointer.x = event.clientX;
    this.#lastPointer.y = event.clientY;

    // Both axes need Math.abs(): checking Y alone (no abs) only counted
    // downward motion as "moving", so pointer paths approaching a trigger
    // from below (negative deltaY, ~0 deltaX) were misread as idle —
    // clearing the safety box immediately and firing #reconcilePointerTarget
    // on every tick instead of after motion settles, which showed up as the
    // dropdown lagging/glitching only on upward approaches.
    const moving = Math.abs(deltaX) >= 1 || Math.abs(deltaY) >= 1;
    activeLink.dataset.safetyBox = `${moving}`;

    clearTimeout(this.#pointerIdleTimer);
    if (moving) {
      this.#pointerIdleTimer = setTimeout(() => {
        if (this.#state.activeItem) {
          this.#state.activeItem.dataset.safetyBox = 'false';
          this.#reconcilePointerTarget();
        }
      }, 50);
    } else {
      this.#reconcilePointerTarget();
    }
  };

  /**
   * Check if the pointer is over a different menu item and trigger activation if so.
   * Works around Safari not re-evaluating hit targets after pseudo-element changes.
   */
  #reconcilePointerTarget() {
    if (performance.now() - this.#activatedAt < RECONCILE_SUPPRESS_MS) return;

    const { x, y } = this.#lastPointer;
    requestAnimationFrame(() => {
      const target = document.elementFromPoint(x, y);
      if (!target) return;
      const listItem = target.closest('.menu-list__list-item');
      if (listItem && !listItem.contains(this.#state.activeItem)) {
        listItem.dispatchEvent(new PointerEvent('pointerenter', { bubbles: false }));
      }
    });
  }

  /**
   * 2026-09-21: root fix for the mega-menu "wrong panel" bug. Every
   * top-level submenu panel (Pokémon, One Piece, Explore, ...) is
   * absolutely positioned at the exact same full-width rectangle (see
   * blocks/_header-menu.liquid) and, for a narrow single-column panel like
   * Pokémon/One Piece, renders identical internal content at identical
   * coordinates. The only thing that makes one panel "correct" over
   * another is whichever top-level trigger the pointer is actually over —
   * but that's normally only checked via discrete pointerenter events
   * (#handleDirectPointerEnter, the framework's own on:pointerenter
   * dispatch), which a fast or diagonal cursor path can skip entirely if
   * it never lands a sampled position on the new trigger's own hit area.
   * Once that happens, the OLD panel stays fully visible and clickable —
   * live-diagnosed as "clicking Sealed under One Piece sometimes navigates
   * to the Pokémon sealed collection instead."
   *
   * This runs on every #onPointerMove tick (while a menu is open — that
   * listener is only ever attached during that window, see
   * #startPointerTracking/#stopPointerTracking): if the pointer is
   * currently over a DIFFERENT top-level trigger than the active one,
   * synthesize a pointerenter on it — same dispatch #reconcilePointerTarget
   * already uses elsewhere in this file.
   *
   * 2026-09-22: now gated on the pointer being inside the trigger row
   * (see #findTriggerAtPoint), not X-only. The original X-only version
   * treated ANY pointer position under a trigger's x-range as intent to
   * switch — including positions deep inside the open panel. Explore's
   * links sit at the page's left content margin (x ≈ 43–481 at 1440px)
   * while its trigger is the rightmost item (x ≈ 586–665), so every
   * trigger → link path crossed under Binders, All Cards, One Piece and
   * Pokémon, and each crossing switched panels; All Cards has no submenu,
   * so crossing it collapsed the menu outright. Live-reproduced with real
   * (CDP) mouse input as `Explore → Binders → All Cards → One Piece →
   * Pokémon` before the pointer ever reached a link. Reported as "the
   * Explore dropdown disappears when moving down toward it".
   *
   * Gating on the row still covers the case this method exists for: a
   * pointer that actually travels to a different trigger is, by
   * definition, inside the row when it gets there, so any sampled
   * pointermove there re-syncs the panel even if the discrete pointerenter
   * was skipped. A pointer that stays in the panel is looking at the
   * active item's own (correct, visible) panel, so there is nothing stale
   * to correct.
   * @param {number} x
   * @param {number} y
   */
  #reconcileActiveTriggerByPosition(x, y) {
    if (!this.#state.activeItem) return;

    const target = this.#findTriggerAtPoint(x, y);
    if (!target) return;

    const activeItem = this.#state.activeItem.closest('.menu-list__list-item');
    if (target === activeItem) return;

    target.dispatchEvent(new PointerEvent('pointerenter', { bubbles: false }));
  }

  /**
   * Finds the flat, currently-visible top-level trigger `<li>` under the
   * given point. Deliberately excludes anything inside the "More" overflow
   * panel (`[slot="overflow"]`/`[slot="more"]`) — that subtree isn't laid
   * out in the flat top-level row this check covers, and already has its
   * own activation path (see findMenuItem()'s dedicated `[slot="more"]`
   * branch).
   *
   * The vertical test uses this component's own box rather than each
   * trigger's: the triggers aren't all the same height (Pokémon/One Piece
   * are taller than the text-only items), and the submenu panels are
   * absolutely positioned so they don't extend this box — its top/bottom
   * is exactly the trigger row. Returns null when the point isn't over a
   * trigger in that row; callers must treat that as "no positive signal".
   * @param {number} x
   * @param {number} y
   * @returns {HTMLElement | null}
   */
  #findTriggerAtPoint(x, y) {
    const row = this.getBoundingClientRect();
    if (y < row.top || y > row.bottom) return null;

    const triggers = this.querySelectorAll('[ref="menuitem"]');
    for (const trigger of triggers) {
      if (trigger.closest('[slot="overflow"]') || trigger.closest('[slot="more"]')) continue;
      const rect = trigger.getBoundingClientRect();
      if (rect.width === 0) continue;
      if (x >= rect.left && x <= rect.right) {
        return /** @type {HTMLElement | null} */ (trigger.closest('.menu-list__list-item'));
      }
    }
    return null;
  }

  /**
   * Begin pointer tracking for the safety box on the newly active item.
   * @param {HTMLElement} item
   * @param {HTMLElement | null} previousItem
   */
  #startPointerTracking(item, previousItem) {
    if (previousItem) {
      this.#stopPointerTracking(previousItem);
    } else {
      document.body.addEventListener('pointermove', this.#onPointerMove);
    }

    const rect = item.getBoundingClientRect();
    const isOverlap = this.headerComponent?.hasAttribute('data-submenu-overlap-bottom-row');
    const boundary = isOverlap ? this.headerComponent?.querySelector('.header__row--top') : this.headerComponent;
    item.style.setProperty('--box-height', `${(boundary?.getBoundingClientRect().bottom ?? 0) - rect.top}px`);
  }

  /**
   * Stop pointer tracking and remove all safety box properties from an item.
   * @param {HTMLElement} item
   */
  #stopPointerTracking(item) {
    window.clearTimeout(this.#pointerIdleTimer);
    this.#pointerIdleTimer = undefined;
    item.style.removeProperty('--box-height');
    delete item.dataset.safetyBox;
  }

  /**
   * Get the overflow menu
   */
  get overflowMenu() {
    return /** @type {HTMLElement | null} */ (this.refs.overflowMenu?.shadowRoot?.querySelector('[part="overflow"]'));
  }

  /**
   * Whether the overflow list is hovered
   * @returns {boolean}
   */
  get overflowListHovered() {
    return this.refs.overflowMenu?.shadowRoot?.querySelector('[part="overflow-list"]')?.matches(':hover') ?? false;
  }

  get headerComponent() {
    return /** @type {HTMLElement | null} */ (this.closest('header-component'));
  }

  /**
   * Activate the selected menu item immediately
   * @param {PointerEvent | FocusEvent} event
   */
  activate = (event) => {
    if (!(event.target instanceof Element) || !this.headerComponent) return;

    // Cancel any pending grace-period close (see DEACTIVATE_GRACE_MS) before
    // the early-return below — a re-entry into the SAME still-active item
    // (the exact case this grace period exists for) hits that early return,
    // so clearing here first is the only place that's guaranteed to run.
    clearTimeout(this.#deactivateTimer);
    this.#deactivateTimer = undefined;

    const item = findMenuItem(event.target);

    // #reconcileActiveTriggerByPosition re-dispatches pointerenter on every
    // pointermove over a trigger; don't let that restart a pending switch to
    // the same item.
    if (item && this.#switchTimer && item === this.#pendingSwitchItem) return;

    // Any other activation supersedes a pending switch — including
    // re-entering the still-active item, which is exactly what a pointer
    // passing through a neighbouring trigger into the open panel does (the
    // panel lives inside the active item's own <li>).
    this.#cancelSwitch();

    if (!item || item == this.#state.activeItem) return;

    if (this.#state.activeItem && !(event instanceof FocusEvent)) {
      this.#scheduleSwitch(event, item);
      return;
    }

    this.#open(event, item);
  };

  /**
   * Commit a pointer-driven switch to `item` only once the pointer shows
   * intent to stay on its trigger — see SWITCH_INTENT_INTERVAL_MS.
   * @param {PointerEvent | FocusEvent} event
   * @param {HTMLElement} item
   */
  #scheduleSwitch(event, item) {
    const trigger = /** @type {Element} */ (event.target).closest('.menu-list__list-item') ?? event.target;
    const startedAt = performance.now();
    // Baseline from the entering event itself when it's a real one:
    // pointerenter fires before the pointermove that would update
    // #lastPointer, so that is one sample stale here.
    let last =
      event instanceof PointerEvent && event.isTrusted
        ? { x: event.clientX, y: event.clientY }
        : { ...this.#lastPointer };

    const check = () => {
      const { x, y } = this.#lastPointer;
      // Hit-test rather than compare against getBoundingClientRect(): the
      // browser's own pointerenter hit-testing works on rounded coordinates,
      // so a pointer resting on a trigger's top/bottom edge can be "inside"
      // for pointerenter yet half a pixel outside the fractional rect.
      if (!(/** @type {Element} */ (trigger).contains(document.elementFromPoint(x, y)))) {
        this.#cancelSwitch();
        return;
      }

      const dx = x - last.x;
      const dy = y - last.y;
      const settled = Math.hypot(dx, dy) < SWITCH_INTENT_SENSITIVITY_PX;
      const headingDown = dy >= 1 && dy >= Math.abs(dx) * SWITCH_INTENT_DOWNWARD_SLOPE;
      if (settled || (!headingDown && performance.now() - startedAt >= SWITCH_INTENT_MAX_MS)) {
        this.#cancelSwitch();
        this.#open(event, item);
        return;
      }
      last = { x, y };
      this.#switchTimer = setTimeout(check, SWITCH_INTENT_INTERVAL_MS);
    };

    this.#pendingSwitchItem = item;
    this.#switchTimer = setTimeout(check, SWITCH_INTENT_INTERVAL_MS);
  }

  #cancelSwitch() {
    clearTimeout(this.#switchTimer);
    this.#switchTimer = undefined;
    this.#pendingSwitchItem = null;
  }

  /**
   * Open `item`'s submenu, replacing whichever item is currently active.
   * @param {PointerEvent | FocusEvent} event
   * @param {HTMLElement} item
   */
  #open = (event, item) => {
    if (!this.headerComponent || item == this.#state.activeItem) return;

    this.#activatedAt = performance.now();

    const isDefaultSlot = event.target.slot === '';

    this.dataset.overflowExpanded = (!isDefaultSlot).toString();

    const previouslyActiveItem = this.#state.activeItem;

    if (previouslyActiveItem) {
      previouslyActiveItem.ariaExpanded = 'false';
    }

    this.#state.activeItem = item;
    this.ariaExpanded = 'true';
    item.ariaExpanded = 'true';

    let submenu = findSubmenu(item);
    const hasSubmenu = Boolean(submenu);

    if (!hasSubmenu && !isDefaultSlot) {
      submenu = this.overflowMenu;
    }

    if (submenu) {
      clearTimeout(this.#hoverDispatchTimer);
      this.#hoverDispatchTimer = undefined;
      const committedItem = item;
      if (event instanceof FocusEvent) {
        this.dispatchEvent(new MegaMenuHoverEvent());
      } else {
        this.#hoverDispatchTimer = setTimeout(() => {
          this.#hoverDispatchTimer = undefined;
          if (this.#state.activeItem === committedItem) {
            this.dispatchEvent(new MegaMenuHoverEvent());
          }
        }, HOVER_COMMIT_DELAY_MS);
      }

      // Mark submenu as active for content-visibility optimization
      submenu.dataset.active = '';

      // Cleanup any existing mutation observer from previous menu activations
      this.#cleanupMutationObserver();

      // Monitor DOM mutations to catch deferred content injection (from section hydration)
      this.#submenuMutationObserver = new MutationObserver(() => {
        requestAnimationFrame(() => {
          // Double requestAnimationFrame to ensure the height is properly calculated and not defaulting to the contain-intrinsic-size
          requestAnimationFrame(() => {
            if (submenu.offsetHeight > 0) {
              this.headerComponent?.style.setProperty('--submenu-height', `${submenu.offsetHeight}px`);
              this.#cleanupMutationObserver();
            }
          });
        });
      });
      this.#submenuMutationObserver.observe(submenu, { childList: true, subtree: true });

      // Auto-disconnect after 500ms to prevent memory leaks
      setTimeout(() => {
        this.#cleanupMutationObserver();
      }, 500);
    }

    let finalHeight = submenu?.offsetHeight || 0;

    // For overflow menu, the height needs to be either content of the submenu or the total height of the menu list links
    //
    // 2026-09-13: added `&& !hasSubmenu` — this branch used to run for EVERY
    // non-default-slot activation, including items reached via
    // findMenuItem()'s `[slot="more"]` redirect (see that function and the
    // "More" trigger in blocks/_header-menu.liquid) whose OWN submenu is a
    // normal, unnested light-DOM sibling (positioned exactly like a
    // top-level item's, not tucked inside the overflow menu's own shadow
    // structure) — for those, submenu.offsetHeight (already captured above)
    // is perfectly valid, and overwriting it with the generic
    // overflow-menu/flat-links height (meant for the *simple-link*
    // overflow case the comment above describes) collapsed the panel to
    // ~0px. Verified live: "Explore" redirected-via-More had aria-expanded
    // correctly set but rendered a 0px-tall (invisible) panel until this
    // guard was added.
    if (!isDefaultSlot && !hasSubmenu) {
      finalHeight = this.#getOverflowListLinksHeight();
    }

    // 2026-09-13: submenu.offsetHeight (captured above) reflects the
    // item's own grid-row-spanned box — see blocks/_header-menu.liquid's
    // `[slot='overflow'] .menu-list__submenu` rule, which spans every row
    // of the left-hand item list so its content lines up in the right-hand
    // grid column — which doesn't always match how tall the actual visible
    // "More" panel renders. Live-verified: when the grid-spanned box
    // measured taller than the panel actually was, --full-open-header-height
    // ended up EXCEEDING the panel's own real height, and the panel's
    // clip-path (.overflow-menu::part(overflow), which shares the same var)
    // became degenerate — clipping the ENTIRE panel to nothing rather than
    // just the overflowing part. Reported as a nav item's link (e.g. "Sell
    // Your Cards" in Explore's redirected panel) vanishing; in fact the
    // WHOLE panel was invisible, that link just happened to be the one
    // being looked for.
    //
    // this.overflowMenu.offsetHeight isn't a fix here — it's circular: that
    // element's own rendered height already depends on the
    // --full-open-header-height we're trying to compute, so it measures
    // whatever the (possibly wrong) PREVIOUS value produced, not the true
    // required height. `[part="overflow-list"]` (the actual scrollable grid
    // holding both columns, `overflow-y: auto`) reports its true CONTENT
    // height via scrollHeight regardless of current clipping/scroll state —
    // not circular — so use that instead, taking whichever measurement is
    // taller so the reveal always covers what's actually rendered.
    if (!isDefaultSlot && hasSubmenu) {
      const overflowListPart = this.refs.overflowMenu?.shadowRoot?.querySelector('[part="overflow-list"]');
      finalHeight = Math.max(finalHeight, overflowListPart?.scrollHeight || 0);
    }

    if (!submenu) {
      // If there is no content to open, don't try to open it
      finalHeight = 0;
    }

    const headerVisibleHeight = this.#getHeaderVisibleHeight();

    this.headerComponent.style.setProperty('--submenu-height', `${finalHeight}px`);
    this.#setFullOpenHeaderHeight(finalHeight, headerVisibleHeight);
    this.style.setProperty('--submenu-opacity', '1');
    this.#startPointerTracking(item, previouslyActiveItem);
  };

  /**
   * Deactivate the active item after a delay
   * @param {PointerEvent | FocusEvent} event
   */
  deactivate(event) {
    if (!(event.target instanceof Element)) return;

    const menu = findSubmenu(this.#state.activeItem);
    const relatedTarget = event.relatedTarget;

    // `menu.contains(relatedTarget)` is the real "is the pointer still inside
    // this item's own submenu" check, and it works for a `pointerleave`
    // exactly as well as a `blur` — Node.contains() walks the full
    // descendant tree, not just an immediate child. This matters most for
    // an overflow-redirected item (e.g. hovering "More" down into Explore's
    // own submenu, see findMenuItem()'s `[slot="more"]` branch): the
    // trigger being left (the "More" `<li>`) and the submenu being entered
    // (Explore's own, real `<li>`) are unrelated DOM subtrees, so a genuine
    // pointerleave fires here, and it needs to be recognized as "still
    // inside the menu" for the panel to survive the move down into it.
    //
    // 2026-09-13: previously this only checked `document.activeElement`
    // (which never reflects pure mouse movement — it tracks keyboard/click
    // focus only, so this was always false for a `pointerleave`) or gated
    // the relatedTarget check on `event.type === 'blur'` (skipping it
    // entirely for pointer events) or required relatedTarget's IMMEDIATE
    // parent to carry `[slot="overflow"]` (true only if the cursor landed
    // exactly on a direct child of the slotted `<li>`, not the mega-menu
    // links/grid one actually hovers in practice). Reported behavior:
    // hovering "More" correctly opened Explore's panel, but moving the
    // pointer down into it closed the panel before a link could be
    // clicked — consistent with none of those three guards ever matching
    // a real mouse transition.
    const isMovingIntoMenu = relatedTarget instanceof Node && Boolean(menu?.contains(relatedTarget));
    const isMovingWithinMenu = event.type === 'blur' && Boolean(menu?.contains(document.activeElement));
    const isMovingToOverflowMenu =
      relatedTarget instanceof Element && Boolean(relatedTarget.closest('[slot="overflow"]'));

    if (isMovingIntoMenu || isMovingWithinMenu || isMovingToOverflowMenu) {
      if (this.#state.activeItem) {
        this.#stopPointerTracking(this.#state.activeItem);
      }
      return;
    }

    // Grace period, not an immediate close — see DEACTIVATE_GRACE_MS. A real
    // pointerleave can still land here (relatedTarget outside the menu
    // entirely) even when the user IS genuinely headed into the panel: a
    // screen-space dead zone between trigger and panel, or the panel's own
    // clip-path reveal not having finished growing yet, both make the
    // panel's real DOM temporarily miss the pointer's hit-test. Any fresh
    // activate() (including re-entering this same item once the pointer
    // actually lands on it) cancels this via #deactivateTimer, so a genuine
    // move-away still closes within DEACTIVATE_GRACE_MS same as before.
    const itemToDeactivate = this.#state.activeItem;
    clearTimeout(this.#deactivateTimer);
    const closeAfterGrace = () => {
      this.#deactivateTimer = setTimeout(() => {
        if (this.#isPointerInBridge()) {
          closeAfterGrace();
          return;
        }
        this.#deactivateTimer = undefined;
        this.#deactivate(itemToDeactivate ?? undefined);
      }, DEACTIVATE_GRACE_MS);
    };
    closeAfterGrace();
  }

  /**
   * 2026-09-22: whether the pointer is in the dead band between the active
   * trigger and its panel — below the trigger's own bottom edge but above
   * the header's bottom edge, where the panel's visible (hit-testable) area
   * starts. Live-measured at 1440px: Explore's trigger ends at y=43, the
   * panel only becomes hoverable at y=66, and a slow diagonal takes ~370ms
   * to cross that 23px — longer than DEACTIVATE_GRACE_MS, so the menu
   * closed mid-approach. deactivate()'s grace timer re-arms while this is
   * true instead of closing. Moving up, sideways along the row, or down
   * past the header all leave the band and close as before.
   *
   * Not applied to overflow ("More") items: their trigger lives inside the
   * overflow panel's own layout, and #overflowSubmenuListener handles that
   * case separately.
   * @returns {boolean}
   */
  #isPointerInBridge() {
    const active = this.#state.activeItem;
    if (!active || !findSubmenu(active) || this.dataset.overflowExpanded === 'true') return false;

    const trigger = active.closest('.menu-list__list-item')?.getBoundingClientRect();
    const isOverlap = this.headerComponent?.hasAttribute('data-submenu-overlap-bottom-row');
    const boundary = isOverlap ? this.headerComponent?.querySelector('.header__row--top') : this.headerComponent;
    const panelTop = boundary?.getBoundingClientRect().bottom;
    if (!trigger || panelTop === undefined) return false;

    const { y } = this.#lastPointer;
    return y >= trigger.bottom && y <= panelTop;
  }

  /**
   * Deactivate the active item immediately
   * @param {HTMLElement | null} [item]
   */
  #deactivate = (item = this.#state.activeItem) => {
    if (!item || item != this.#state.activeItem) return;

    // Don't deactivate if the overflow menu or overflow list is still being hovered
    if (this.overflowListHovered || this.overflowMenu?.matches(':hover')) return;

    this.#cancelSwitch();
    clearTimeout(this.#hoverDispatchTimer);
    this.#hoverDispatchTimer = undefined;

    this.headerComponent?.style.setProperty('--submenu-height', '0px');
    this.#setFullOpenHeaderHeight(0, 0);
    this.style.setProperty('--submenu-opacity', '0');
    this.dataset.overflowExpanded = 'false';

    const submenu = findSubmenu(item);

    document.body.removeEventListener('pointermove', this.#onPointerMove);
    this.#stopPointerTracking(item);

    this.#state.activeItem = null;
    this.ariaExpanded = 'false';
    item.ariaExpanded = 'false';

    // Remove active state from submenu after animation completes
    if (submenu) {
      delete submenu.dataset.active;
    }
  };

  #getOverflowListLinksHeight() {
    const slottedMenuLinks = this.overflowMenu?.querySelector('slot')?.assignedElements();
    if (!slottedMenuLinks) return this.overflowMenu?.offsetHeight || 0;

    /**
     * @param {(submenu: HTMLElement) => void} cb
     */
    const mapSubmenus = (cb) => {
      slottedMenuLinks.forEach((link) => {
        const submenu = /** @type {HTMLElement | null} */ (link.querySelector('[ref="submenu[]"]'));
        if (submenu) {
          cb(submenu);
        }
      });
    };

    mapSubmenus((submenu) => {
      submenu.style.setProperty('display', 'none');
    });
    const height = this.overflowMenu?.offsetHeight || 0;
    mapSubmenus((submenu) => {
      submenu.style.removeProperty('display');
    });
    return height;
  }

  /**
   * Read the visible header height before submenu height writes invalidate layout.
   * @returns {number}
   */
  #getHeaderVisibleHeight() {
    if (!this.headerComponent) return 0;

    const isOverlapSituation = this.headerComponent.hasAttribute('data-submenu-overlap-bottom-row');

    return isOverlapSituation && this.headerComponent.offsetHeight > 0
      ? /** @type {HTMLElement | null} */ (this.headerComponent.querySelector('.header__row--top'))?.offsetHeight ?? 0
      : this.headerComponent.offsetHeight;
  }

  /**
   * Calculate and set the full open header height. If the submenu is not open, the full open header height is 0.
   * @param {number} submenuHeight
   * @param {number} headerVisibleHeight
   */
  #setFullOpenHeaderHeight(submenuHeight, headerVisibleHeight) {
    if (!this.headerComponent) return;

    const nothingToOpen = submenuHeight === 0;
    const fullOpenHeaderHeight = nothingToOpen ? 0 : submenuHeight + headerVisibleHeight;

    this.headerComponent?.style.setProperty('--full-open-header-height', `${fullOpenHeaderHeight}px`);
  }

  /**
   * Preload images that are set to load lazily.
   */
  #preloadImages = () => {
    const images = this.querySelectorAll('img[loading="lazy"]');
    images?.forEach((image) => image.removeAttribute('loading'));
  };

  #cleanupMutationObserver() {
    this.#submenuMutationObserver?.disconnect();
    this.#submenuMutationObserver = null;
  }
}

if (!customElements.get('header-menu')) {
  customElements.define('header-menu', HeaderMenu);
}

/**
 * Find the closest menu item.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findMenuItem(element) {
  if (!(element instanceof Element)) return null;

  if (element?.matches('[slot="more"]')) {
    // Select the first overflowing menu item when hovering over the "More" item
    return findMenuItem(element.parentElement?.querySelector('[slot="overflow"]'));
  }

  return element?.querySelector('[ref="menuitem"]');
}

/**
 * Find the closest submenu.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findSubmenu(element) {
  const submenu = element?.parentElement?.querySelector('[ref="submenu[]"]');
  return submenu instanceof HTMLElement ? submenu : null;
}
